/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */

import { jsx } from "@emotion/core"
import {
  useRef,
  useEffect,
  useState,
  FC,
  ReactNode,
  CSSProperties,
} from "react"
import { Tooltip } from "@duxton-web/ui"

export interface IOverflowTip {
  children: ReactNode
  style?: CSSProperties
  className?: string
}

export const OverflowTip: FC<IOverflowTip> = ({
  children,
  style,
  className,
}: IOverflowTip) => {
  const textContainerRef = useRef<HTMLDivElement>(null)

  const [isOverflow, setIsOverflow] = useState(false)

  // compare text element scroll width with its client width to know if the text is overflow or not
  const compareSize = () => {
    if (textContainerRef.current) {
      setIsOverflow(
        textContainerRef.current.scrollWidth >
          textContainerRef.current.clientWidth,
      )
    }
  }

  // do comparation once and everytime screen width changes
  useEffect(() => {
    // small hack for duxton, because we need to wait until it is loaded
    setTimeout(() => {
      compareSize()
    }, 0)

    window.addEventListener("resize", compareSize)

    return () => {
      window.removeEventListener("resize", compareSize)
    }
  }, [])

  return (
    <Tooltip
      title={children}
      placement="bottomLeft"
      overlayStyle={
        !isOverflow
          ? {
              display: "none",
            }
          : {
              wordBreak: "break-word",
            }
      }
    >
      <div
        ref={textContainerRef}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...style,
        }}
        className={className}
      >
        {children}
      </div>
    </Tooltip>
  )
}
